/**
 * What happens once a login call finally returns a session (09 § 4.2).
 *
 * Shared by the four routes that can produce one — the OTP step, the two
 * detours and the OAuth callback — so the member routing and the first-touch
 * hand-over cannot drift between them.
 */

import { readFirstTouch, type AuthState } from '@cowoo/shared';
import { api } from '../../lib/api';
import { useSessionStore } from '../../lib/session';
import { forgetPendingLogin, takeReturnTo } from './pending';

type Authenticated = Extract<AuthState, { status: 'authenticated' }>;

/**
 * Steps 1–3 of § 4.2. Returns the path to land on.
 *
 * `GET /me` is what creates the user server-side (BR-ACC-01), so it runs before
 * anything reads the profile.
 *
 * **Nothing in here may throw.** By the time it runs the login has succeeded
 * and the token is stored, so a failure of our own API is not a failed login —
 * but the callers are code screens whose `catch` turns anything thrown into
 * `authErrors.*`, and the user would be told their code was wrong while they
 * are, in fact, signed in. The routing question degrades to the default
 * instead; the funnel's own guards ask again on the next screen.
 */
export async function afterAuthentication(state: Authenticated): Promise<string> {
  useSessionStore.getState().setSession(state.token, state.userId);
  forgetPendingLogin();

  const returnTo = takeReturnTo();
  try {
    const { data: me } = await api.GET('/me');
    if (!me) return returnTo;

    // § 14 — the site hands first-touch over as `?ft=`. The server ignores a
    // second one (D-094), so this does not need to know whether it already ran.
    const firstTouch = readFirstTouch();
    if (firstTouch) await api.POST('/me/first-touch', { body: firstTouch });

    return hasMembership(me.access.contract) ? '/mitglied' : returnTo;
  } catch {
    return returnTo;
  }
}

/**
 * § 4.2 step 2 — a second access-granting contract would be refused with
 * `already_member`, so the funnel is not offered to a member in the first place.
 *
 * BR-CON-02 allows a *switch* while a cancelled contract still runs, but only
 * while no successor exists — and `Me.access.contract` carries no successor
 * (open item OI-F-14). A cancelled contract therefore lands on `/mitglied`,
 * which is where the switch lives anyway; the other reading would offer a
 * funnel that the server may then refuse.
 */
function hasMembership(contract: { status: string } | null): boolean {
  return contract !== null;
}
